import { ArrowRight, Check, MapPin } from "lucide-react";
import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";
import { MaskedLines, FadeUp, SectionTag } from "@/components/Reveal";
import { IMAGES, SERVICE_LD, FAQ_LD, TESTIMONIALS } from "@/data/content";
import { trackMicroConversion } from "@/lib/tracking";

const scrollToForm = (e) => {
  e.preventDefault();
  trackMicroConversion("cta");
  document.getElementById("anfrage")?.scrollIntoView({ behavior: "smooth" });
};

const FAQS = [
  { q: "Was kostet eine freie Trauung?", a: "Jede Trauung ist individuell. Nach dem kostenlosen Kennenlerngespräch erhaltet ihr ein transparentes, verbindliches Angebot, das genau zu eurem Tag passt – ohne versteckte Kosten." },
  { q: "Wie früh sollten wir unseren Termin anfragen?", a: "Am besten sechs bis zwölf Monate vorher, besonders für Sommersamstage. Kurzfristige Anfragen sind ebenso willkommen – wir sagen euch ehrlich, was möglich ist." },
  { q: "Findet das Kennenlerngespräch kostenlos statt?", a: "Ja. Das Kennenlerngespräch ist immer kostenlos und unverbindlich – bei euch zu Hause, an eurem Lieblingsort oder per Video." },
  { q: "Wo sind freie Trauungen möglich?", a: "Überall: im eigenen Garten, auf Gutshöfen am Niederrhein, im Grünen, am Rhein oder in einer Location eurer Wahl. Wir kommen dorthin, wo eure Geschichte hingehört." },
  { q: "Kommt ihr auch außerhalb von Krefeld?", a: "Ja. Wir begleiten Trauungen in Krefeld, am Niederrhein, in ganz NRW – und deutschlandweit nach Absprache." },
  { q: "Wie persönlich wird unsere Traurede?", a: "Jede Rede entsteht nur für euch – aus euren Gesprächen und Geschichten, ohne Vorlage. Ihr lest die Rede vorab, und wir feilen gemeinsam an jedem Satz." },
  { q: "Können Musik und besondere Rituale eingebaut werden?", a: "Sehr gern. Mark singt euer Lied live, Angela spielt es am Saxophon. Auch Rituale, Beiträge der Gäste oder eure eigene Musik binden wir ein." },
  { q: "Was passiert nach unserer Anfrage?", a: "Ihr erhaltet eine persönliche Rückmeldung – in der Regel innerhalb von 24 Stunden. Danach vereinbaren wir euer kostenloses Kennenlerngespräch." },
];

const TRUST = [
  "Persönlich geschriebene Traurede",
  "Individuelle Planung eurer Zeremonie",
  "Krefeld · Niederrhein · NRW",
  "Auf Wunsch Live-Gesang oder Saxophon",
];

const ABLAUF = [
  { title: "Kostenlos kennenlernen", text: "Ihr erzählt uns von eurer Hochzeit, wir hören zu. Unverbindlich und ohne Verpflichtung – danach wisst ihr, ob die Chemie stimmt." },
  { title: "Eure Geschichte & Wünsche besprechen", text: "Wie habt ihr euch kennengelernt? Was macht euch aus? Aus euren Antworten entsteht das Fundament eurer Trauung." },
  { title: "Rede & Zeremonie entwickeln", text: "Wir schreiben eure Traurede nur für euch und stimmen Ablauf, Musik und Rituale mit euch ab – bis jeder Satz sitzt." },
  { title: "Eure freie Trauung erleben", text: "Am großen Tag führen wir ruhig und sicher durch eure Zeremonie. Der Moment gehört euch – wir halten ihn." },
];

const LOCATIONS = [
  { title: "Im Grünen", text: "Gärten, Botanischer Garten, Stadtwald – freie Trauungen unter freiem Himmel" },
  { title: "Historisch", text: "im Umfeld von Burg Linn oder in den Backstein-Ensembles der Stadt" },
  { title: "Am Wasser", text: "Rheinwiesen und Uerdinger Rheinfront – besonders zur Abendstunde" },
  { title: "Rustikal & modern", text: "Gutshöfe und Scheunen am Niederrhein, Hotels und Lofts in der Stadt" },
];

const FreieTrauungKrefeld = () => (
  <div data-testid="krefeld-page">
    <SEO
      title="Freie Trauung Krefeld | Persönlicher Trauredner"
      description="Freie Trauung in Krefeld & am Niederrhein: persönliche Traurede von Trauredner Mark Daviz & Rednerin Angela Puxi – auf Wunsch mit Live-Gesang & Saxophon. Jetzt kostenloses Kennenlernen anfragen."
      path="/freie-trauung-krefeld"
      image={IMAGES.archBlush}
      jsonLd={[SERVICE_LD(window.location.origin), FAQ_LD(FAQS)]}
    />

    {/* HERO */}
    <section className="relative flex min-h-[92svh] items-end overflow-hidden">
      <img
        src={IMAGES.archBlush}
        alt="Freie Trauung in Krefeld – festlich geschmückter Traupfad mit blühenden Kirschbäumen"
        fetchPriority="high"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/40 to-ink/25" />
      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-14 pt-36 sm:px-8">
        <FadeUp>
          <p className="mb-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-sand">
            <span className="h-px w-10 bg-sand" /> Trauredner Krefeld · Niederrhein · NRW
          </p>
        </FadeUp>
        <MaskedLines
          lines={["Freie Trauung in Krefeld –", "persönlich, emotional", "und ganz nach euch"]}
          className="font-serif text-4xl leading-[1.06] text-creme sm:text-6xl lg:text-7xl"
          delay={0.2}
        />
        <FadeUp delay={0.55}>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-creme/90 sm:text-lg">
            Eine Zeremonie, die eure Geschichte erzählt – mit persönlichen Worten
            und auf Wunsch mit Live-Gesang oder Saxophon.
          </p>
          <div className="mt-9 flex flex-col gap-4 sm:flex-row">
            <a
              href="#anfrage"
              onClick={scrollToForm}
              data-testid="krefeld-hero-cta"
              className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-creme px-9 py-4 text-base font-bold text-ink transition-transform duration-300 hover:scale-[1.04] sm:w-auto"
            >
              Kostenloses Kennenlerngespräch anfragen <ArrowRight size={17} />
            </a>
            <a
              href="#anfrage"
              onClick={scrollToForm}
              data-testid="krefeld-hero-cta-secondary"
              className="inline-flex w-full items-center justify-center gap-3 rounded-full border border-creme/40 px-9 py-4 text-base font-semibold text-creme backdrop-blur-sm transition-colors duration-300 hover:border-sand hover:text-sand sm:w-auto"
            >
              Verfügbarkeit prüfen
            </a>
          </div>
          <p className="mt-5 text-xs font-semibold uppercase tracking-[0.22em] text-creme/70">
            Kostenlos · unverbindlich · persönlich
          </p>
        </FadeUp>
      </div>
    </section>

    {/* VERTRAUEN */}
    <section className="border-b border-sand/70 bg-parch">
      <div className="mx-auto grid max-w-7xl gap-x-10 gap-y-5 px-5 py-10 sm:grid-cols-2 sm:px-8 lg:grid-cols-4">
        {TRUST.map((t, i) => (
          <FadeUp key={t} delay={i * 0.08}>
            <p className="flex items-start gap-3 text-sm font-medium leading-snug text-ink">
              <Check size={17} className="mt-0.5 shrink-0 text-moss" /> {t}
            </p>
          </FadeUp>
        ))}
      </div>
    </section>

    {/* PROBLEM & NUTZEN */}
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <FadeUp>
            <SectionTag>Warum frei trauen?</SectionTag>
            <h2 className="mt-6 font-serif text-4xl leading-[1.1] sm:text-5xl">
              Keine Zeremonie<br />von der Stange –<br />
              <span className="font-accent italic text-rust">sondern eure.</span>
            </h2>
          </FadeUp>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <FadeUp delay={0.15}>
            <p className="text-lg leading-relaxed text-stone">
              Viele Trauungen klingen gleich: dieselben Worte, dieselben Floskeln, dieselbe
              Distanz. Eure Beziehung ist aber nicht austauschbar – und eure Zeremonie sollte es
              auch nicht sein.
            </p>
            <p className="mt-6 text-lg leading-relaxed text-stone">
              Bei uns bekommt ihr eine Trauung, die nach euch klingt: nach eurem Kennenlernen,
              eurem Lachen, euren kleinen Ritualen. Eure Gäste sollen nicht höflich zuhören –
              sie sollen lachen, weinen und am Ende sagen: „Das wart ganz ihr."
            </p>
          </FadeUp>
        </div>
      </div>
    </section>

    {/* ABLAUF */}
    <section className="bg-parch py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <FadeUp className="mb-14 max-w-2xl">
          <SectionTag>Ganz einfach</SectionTag>
          <h2 className="mt-5 font-serif text-4xl sm:text-5xl">So entsteht eure<br />freie Trauung</h2>
        </FadeUp>
        <div className="grid gap-x-12 md:grid-cols-2">
          {ABLAUF.map((s, i) => (
            <FadeUp key={s.title} delay={(i % 2) * 0.08}>
              <div className="flex gap-6 border-b border-sand/70 py-7">
                <span className="font-accent text-3xl italic leading-none text-sand">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h3 className="font-serif text-xl">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-stone">{s.text}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp className="mt-12">
          <a
            href="#anfrage"
            onClick={scrollToForm}
            data-testid="krefeld-ablauf-cta"
            className="inline-flex items-center gap-3 rounded-full bg-moss px-8 py-4 text-sm font-bold text-creme transition-colors duration-300 hover:bg-mossdeep"
          >
            Schritt 1 starten – kostenlos kennenlernen <ArrowRight size={15} />
          </a>
        </FadeUp>
      </div>
    </section>

    {/* USP: WORTE + MUSIK */}
    <section className="relative overflow-hidden py-24 sm:py-32">
      <img src={IMAGES.microphone} alt="Live-Gesang und Saxophon zur freien Trauung" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-mossdeep/90" />
      <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
        <FadeUp>
          <SectionTag dark>Das Besondere</SectionTag>
          <h2 className="mt-6 font-serif text-4xl leading-[1.08] text-creme sm:text-5xl">
            Wenn Worte und Musik<br />
            <span className="font-accent italic text-sand">eure Geschichte erzählen</span>
          </h2>
          <p className="mx-auto mt-7 max-w-2xl font-semibold leading-relaxed text-sand">
            Unser besonderes Extra: Eure Geschichte und euer Lied – live.
          </p>
          <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-creme/80">
            Wir sind nicht nur Redner – wir sind Musiker. Mark Daviz singt euer Lied live zu
            eurer Zeremonie, Angela Puxi spielt es am Saxophon. Auf Wunsch komplettiert der
            jeweils andere eure Feier. Kein Playback, keine Konserve: ein Moment, der sich
            anfühlt wie euer Lied – weil er es ist.
          </p>
          <a
            href="#anfrage"
            onClick={scrollToForm}
            data-testid="krefeld-usp-cta"
            className="mt-10 inline-flex items-center gap-3 rounded-full bg-creme px-8 py-4 text-sm font-bold text-mossdeep transition-transform duration-300 hover:scale-[1.03]"
          >
            Verfügbarkeit prüfen <ArrowRight size={15} />
          </a>
        </FadeUp>
      </div>
    </section>

    {/* PERSÖNLICHKEIT */}
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
      <FadeUp className="mb-14">
        <SectionTag>Persönlich</SectionTag>
        <h2 className="mt-5 font-serif text-4xl sm:text-5xl">Wer euch an eurem<br />großen Tag begleitet</h2>
      </FadeUp>
      <div className="grid gap-10 md:grid-cols-2">
        {[
          {
            name: "Mark Daviz",
            role: "Freier Trauredner & Sänger",
            img: IMAGES.mark,
            pos: "center 12%",
            text: "Über vier Oktaven Soul und Funk, Jahre auf internationalen Bühnen – und die Ruhe, im richtigen Moment die richtigen Worte zu finden. Mark hört zu, bevor er schreibt.",
          },
          {
            name: "Angela Puxi",
            role: "Freie Rednerin & Saxophonistin",
            img: IMAGES.angela,
            pos: "center 22%",
            text: "Studierte Jazz-Saxophonistin mit sardischem Temperament. Angela erzählt eure Geschichte warm, natürlich und ohne Effekthascherei – so, dass sich jeder darin wiederfindet.",
          },
        ].map((p, i) => (
          <FadeUp key={p.name} delay={i * 0.12}>
            <div className="flex h-full flex-col border border-sand bg-parch/50 sm:flex-row">
              <img
                src={p.img}
                alt={`${p.name} – ${p.role} für freie Trauungen in Krefeld`}
                loading="lazy"
                style={{ objectPosition: p.pos }}
                className="aspect-[4/5] w-full object-cover sm:w-44 sm:shrink-0"
              />
              <div className="p-7">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-rust">{p.role}</p>
                <h3 className="mt-2 font-serif text-2xl">{p.name}</h3>
                <p className="mt-3 text-sm leading-relaxed text-stone">{p.text}</p>
              </div>
            </div>
          </FadeUp>
        ))}
      </div>
      <FadeUp className="mt-10 max-w-2xl">
        <p className="leading-relaxed text-stone">
          Uns ist wichtig: Die Chemie zwischen euch und eurem Redner entscheidet über eure
          Zeremonie. Deshalb beginnt alles mit einem kostenlosen Kennenlerngespräch – ihr merkt
          dabei schnell, ob wir die richtigen Menschen für euren Tag sind.
        </p>
        <a
          href="#anfrage"
          onClick={scrollToForm}
          data-testid="krefeld-personen-cta"
          className="mt-7 inline-flex items-center gap-3 rounded-full bg-moss px-8 py-4 text-sm font-bold text-creme transition-colors duration-300 hover:bg-mossdeep"
        >
          Uns kennenlernen <ArrowRight size={15} />
        </a>
      </FadeUp>
    </section>

    {/* KUNDENSTIMMEN */}
    <section className="bg-parch py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <FadeUp className="mb-14 max-w-2xl">
          <SectionTag>Kundenstimmen</SectionTag>
          <h2 className="mt-5 font-serif text-4xl sm:text-5xl">Was Paare<br />über uns sagen</h2>
        </FadeUp>
        <div className="grid gap-6 md:grid-cols-2">
          {TESTIMONIALS.slice(0, 2).map((t, i) => (
            <FadeUp key={t.name} delay={i * 0.1}>
              <figure
                data-testid={`krefeld-testimonial-${i}`}
                className="flex h-full flex-col justify-between border border-sand bg-creme p-8"
              >
                <blockquote className="font-accent text-xl italic leading-relaxed text-ink sm:text-2xl">
                  „{t.quote}"
                </blockquote>
                <figcaption className="mt-6 text-xs font-semibold uppercase tracking-[0.2em] text-rust">
                  {t.name} · <span className="font-medium normal-case tracking-normal text-stone">{t.context}</span>
                </figcaption>
              </figure>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    {/* LOKALER BEZUG */}
    <section className="bg-creme py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <FadeUp className="mb-14 max-w-2xl">
          <SectionTag>Krefeld & Niederrhein</SectionTag>
          <h2 className="mt-5 font-serif text-4xl sm:text-5xl">Eure Trauung –<br />hier bei euch zu Hause</h2>
          <p className="mt-5 leading-relaxed text-stone">
            Wir kommen aus Krefeld und begleiten Trauungen in der ganzen Region – in der Stadt,
            am Niederrhein und in ganz NRW. Diese Orte eignen sich besonders für freie Trauungen:
          </p>
        </FadeUp>
        <div className="grid gap-6 sm:grid-cols-2">
          {LOCATIONS.map((l, i) => (
            <FadeUp key={l.title} delay={i * 0.08}>
              <div data-testid={`krefeld-location-${i}`} className="flex h-full items-start gap-5 border border-sand bg-parch/50 p-7">
                <MapPin size={20} className="mt-1 shrink-0 text-rust" />
                <div>
                  <h3 className="font-serif text-xl">{l.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-stone">{l.text}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    {/* PREIS */}
    <section className="mx-auto max-w-4xl px-5 py-24 text-center sm:px-8 sm:py-28">
      <FadeUp>
        <SectionTag>Transparenz</SectionTag>
        <h2 className="mt-5 font-serif text-3xl sm:text-4xl">Was kostet eure freie Trauung?</h2>
        <p className="mx-auto mt-5 max-w-xl leading-relaxed text-stone">
          Nach unserem kostenlosen Kennenlerngespräch erhaltet ihr ein transparentes und
          verbindliches Angebot passend zu eurer Trauung. Fahrtkosten sind in Krefeld und
          Umgebung inklusive.
        </p>
        <a
          href="#anfrage"
          onClick={scrollToForm}
          data-testid="krefeld-preis-cta"
          className="mt-8 inline-flex items-center gap-3 rounded-full bg-moss px-8 py-4 text-sm font-bold text-creme transition-colors duration-300 hover:bg-mossdeep"
        >
          Unverbindlich anfragen <ArrowRight size={15} />
        </a>
      </FadeUp>
    </section>

    {/* FAQ */}
    <section className="mx-auto max-w-4xl px-5 pb-24 sm:px-8 sm:pb-32">
      <FadeUp className="mb-12">
        <SectionTag>Häufige Fragen</SectionTag>
        <h2 className="mt-5 font-serif text-4xl sm:text-5xl">Kurz beantwortet</h2>
      </FadeUp>
      <div className="border-t border-sand">
        {FAQS.map((f, i) => (
          <FadeUp key={i} delay={Math.min(i * 0.05, 0.25)}>
            <div data-testid={`krefeld-faq-${i}`} className="border-b border-sand py-7">
              <h3 className="font-serif text-xl sm:text-2xl">{f.q}</h3>
              <p className="mt-3 leading-relaxed text-stone">{f.a}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </section>

    {/* KONTAKT */}
    <section id="anfrage" className="scroll-mt-20 bg-mossdeep py-24 text-creme sm:py-32">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <FadeUp className="mb-12 text-center">
          <SectionTag dark>Kontakt</SectionTag>
          <h2 className="mt-6 font-serif text-4xl leading-[1.08] sm:text-5xl">
            Kostenloses<br />
            <span className="font-accent italic text-sand">Kennenlerngespräch anfragen</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl leading-relaxed text-creme/75">
            Schickt uns kurz die wichtigsten Informationen zu eurer Hochzeit.
            Wir melden uns persönlich und unverbindlich bei euch.
          </p>
        </FadeUp>
        <FadeUp delay={0.15}>
          <div className="bg-creme p-6 text-ink sm:p-10">
            <ContactForm />
          </div>
        </FadeUp>
      </div>
    </section>
  </div>
);

export default FreieTrauungKrefeld;
